import type { Href } from "expo-router";
import { router } from "expo-router";
import { useState } from "react";
import { View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Image from "$src/core/components/atoms/Image/Image";
import { AnimatedScrollView } from "$src/core/components/atoms/ScrollView/ScrollView";
import Text from "$src/core/components/atoms/Text/Text";
import TextInput from "$src/core/components/atoms/TextInput/TextInput";
import { TEST_IDS } from "$src/core/constants/test-ids";
import useAnalytics from "$src/core/hooks/use-analytics";
import useAuth from "$src/core/hooks/use-auth";
import useConfig from "$src/core/hooks/use-config";
import useStackHeader from "$src/core/hooks/use-stack-header";
import useTranslation from "$src/core/hooks/use-translation";
import Alerter from "$src/core/services/alerting";

import $ from "./RegisterPage.styles";
import type { RegisterPageProps } from "./types";

const RegisterPage: React.FC<RegisterPageProps> = ({ redirect }) => {
  const { t } = useTranslation();
  const { logo } = useConfig();
  const { register } = useAuth();
  const { trackEvent } = useAnalytics();
  useStackHeader({
    left: "back",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleRegister = async () => {
    try {
      setIsLoading(true);

      await register(email, password, {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      });
      trackEvent("register", { result: "successful" });

      if (redirect) {
        router.replace(redirect as Href);
      } else {
        router.navigate("/profiles");
      }
    } catch {
      trackEvent("register", { result: "error" });
      Alerter.error(t("ERROR/AUTH/REGISTER"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatedScrollView
      contentContainerStyle={$.container}
      accessibilityRole="list"
      scrollEventThrottle={16}
    >
      <View style={$.form}>
        <Image
          source={logo}
          style={$.logo}
          contentFit="contain"
          contentPosition="left"
          accessibilityRole="image"
          accessibilityLabel={t("COMMON/LOGO_ACCESSIBILITY_LABEL")}
        />

        <Text preset="heading1" accessibilityRole="text" style={$.title}>
          {t("REGISTER_PAGE/HEADING")}
        </Text>

        <View style={$.field}>
          <Text preset="heading5" accessibilityRole="text">
            {t("AUTH/FIRST_NAME/INPUT/LABEL")}
          </Text>
          <TextInput
            autoCapitalize="words"
            autoComplete="given-name"
            placeholder={t("AUTH/FIRST_NAME/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("AUTH/FIRST_NAME/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={setFirstName}
            testID={TEST_IDS.register.firstNameInput}
          />
        </View>
        <View style={$.field}>
          <Text preset="heading5" accessibilityRole="text">
            {t("AUTH/LAST_NAME/INPUT/LABEL")}
          </Text>
          <TextInput
            autoCapitalize="words"
            autoComplete="family-name"
            placeholder={t("AUTH/LAST_NAME/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("AUTH/LAST_NAME/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={setLastName}
            testID={TEST_IDS.register.lastNameInput}
          />
        </View>
        <View style={$.field}>
          <Text preset="heading5" accessibilityRole="text">
            {t("AUTH/EMAIL/INPUT/LABEL")}
          </Text>
          <TextInput
            autoCapitalize="none"
            autoComplete="email"
            placeholder={t("AUTH/EMAIL/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("AUTH/EMAIL/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={setEmail}
            testID={TEST_IDS.register.emailInput}
          />
        </View>
        <View style={$.field}>
          <Text preset="heading5" accessibilityRole="text">
            {t("AUTH/PASSWORD/INPUT/LABEL")}
          </Text>
          <TextInput
            autoCapitalize="none"
            autoComplete="new-password"
            placeholder={t("AUTH/PASSWORD/INPUT/PLACEHOLDER")}
            accessibilityLabel={t("AUTH/PASSWORD/INPUT/LABEL")}
            accessibilityRole="text"
            onChangeText={setPassword}
            secureTextEntry
            testID={TEST_IDS.register.passwordInput}
          />
        </View>

        <Button
          name="register"
          preset="primary"
          onPress={handleRegister}
          label={t("REGISTER_PAGE/CTA/LABEL")}
          isLoading={isLoading}
          isDisabled={!email || !password}
          style={$.button}
          testID={TEST_IDS.register.createAccountButton}
        />

        <Button
          name="sign_in_link"
          preset="link"
          onPress={() =>
            router.replace(redirect ? `/login?redirect=${redirect}` : "/login")
          }
          label={t("REGISTER_PAGE/SIGN_IN_LINK")}
          style={$.link}
        />
      </View>
    </AnimatedScrollView>
  );
};

export default RegisterPage;
